// src/pages/Dashboard.tsx

import React, { useCallback, useEffect, useRef, useState } from "react";
// —— 这两行必须在最顶（react-date-range 样式）——
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import { DateRange } from "react-date-range";
import { startOfDay, endOfDay, format, subDays } from "date-fns";
import { saveAs } from "file-saver";
import { ListTodo, CheckCheck } from "lucide-react";
import { useAuth } from '../hooks/useAuth';
import ScheduledJobs from "../components/ScheduledJobs";
import RecentCallsList from "../components/RecentCallsList";
import CallDetails from "../components/CallDetails";

import { fetchSummary, fetchTranscript } from "../api/fetchData";
import type { FullCallData, SummaryCall } from "../types";
import type { Range, RangeKeyDict } from "react-date-range";

function isInRange(call: SummaryCall, start: Date, end: Date) {
  const d = new Date(call.received_at);
  return d >= startOfDay(start) && d <= endOfDay(end);
}

const cardClass =
  "bg-white rounded-3xl shadow-sm border p-6 h-full flex flex-col";

function AutoFitCalendar({
  value,
  onChange,
}: {
  value: Range[];
  onChange: (r: Range[]) => void;
}) {
  const viewRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  
  const recalc = useCallback(() => {
    const view = viewRef.current;
    const inner = innerRef.current;
    if (!view || !inner) return;

    inner.style.transform = `scale(1)`;
    inner.style.top = "0px";
    inner.style.left = "0px";

    const viewBox = view.getBoundingClientRect();
    const innerBox = inner.getBoundingClientRect();

    const pad = 0;
    const availW = Math.max(viewBox.width - pad * 2, 100);
    const availH = Math.max(viewBox.height - pad * 2, 100);

    const s = Math.min(availW / innerBox.width, availH / innerBox.height);
    const clamped = Math.max(1, Math.min(30.0, s));
    setScale(clamped);

    const scaledW = innerBox.width * clamped;
    const scaledH = innerBox.height * clamped;
    const offsetX = Math.max((viewBox.width - scaledW) / 2, 0);
    const offsetY = Math.max((viewBox.height - scaledH) / 2, 0);

    inner.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${clamped})`;
    inner.style.transformOrigin = "top left";
  }, []);

  useEffect(() => {
    recalc();
    const ro = new ResizeObserver(recalc);
    if (viewRef.current) ro.observe(viewRef.current);
    window.addEventListener("resize", recalc);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recalc);
    };
  }, [recalc]);

  return (
    <div ref={viewRef} className="relative h-full w-full overflow-hidden">
      <div ref={innerRef} className="absolute top-0 left-0">
        <DateRange
          editableDateInputs
          moveRangeOnFirstSelection={false}
          ranges={value}
          onChange={(itm: RangeKeyDict) => onChange([itm.selection])}
          months={1}
          direction="horizontal"
          showDateDisplay={false}
          rangeColors={["#4f46e5"]}
          maxDate={new Date()}
        />
      </div>
    </div>
  );
}

/* ---------- 页面 ---------- */
export default function Dashboard() {
  const { user, logout } = useAuth();
  // —— 数据状态 —— //
  const [allCalls, setAllCalls] = useState<SummaryCall[]>([]);
  const [calls, setCalls] = useState<SummaryCall[]>([]);
  const [selectedCall, setSelectedCall] = useState<FullCallData | null>(null);
  const [selectedCallId, setSelectedCallId] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);

  // —— 统计 —— //
  const [callTypes, setCallTypes] = useState<Record<string, number>>({});
  const [statusTypes, setStatusTypes] = useState<Record<string, number>>({});

  // —— Token 总计 —— //
  const [totalPromptTokens, setTotalPromptTokens] = useState(0);
  const [totalCompletionTokens, setTotalCompletionTokens] = useState(0);

  // —— ScheduledJobs 下拉 —— //
  const [showJobsDropdown, setShowJobsDropdown] = useState(false);
  const jobsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (jobsRef.current && !jobsRef.current.contains(e.target as Node)) {
        setShowJobsDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // —— 日期筛选（左侧日历：选择即生效） —— //
  const [dateRange, setDateRange] = useState<Range[] | undefined>(undefined);
  const [calendarRange, setCalendarRange] = useState<Range[]>([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);

  // 首次加载
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const { calls: fetched } = await fetchSummary(user.token);
        setAllCalls(fetched);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // 过滤 & 统计 & 排序 & token 汇总
  useEffect(() => {
    let filtered = allCalls;
    if (dateRange?.[0].startDate && dateRange[0].endDate) {
      const { startDate, endDate } = dateRange[0];
      filtered = allCalls.filter((c) => isInRange(c, startDate!, endDate!));
    }

    const ct: Record<string, number> = {};
    const st: Record<string, number> = {};
    let promptSum = 0,
      completionSum = 0;

    filtered.forEach((c) => {
      ct[c.metadata.type] = (ct[c.metadata.type] || 0) + 1;
      st[c.status] = (st[c.status] || 0) + 1;

      const u = c.usage ?? {};
      const prompt = u.llm_prompt_tokens ?? 0;
      const cached = u.llm_prompt_cached_tokens ?? 0;
      promptSum += prompt + cached;
      completionSum += u.llm_completion_tokens ?? 0;
    });

    setCallTypes(ct);
    setStatusTypes(st);
    setTotalPromptTokens(promptSum);
    setTotalCompletionTokens(completionSum);

    const sorted = [...filtered].sort(
      (a, b) =>
        new Date(b.received_at).getTime() - new Date(a.received_at).getTime()
    );
    setCalls(sorted);
  }, [allCalls, dateRange]);

  // 选中一条通话
  const handleSelectCall = async (call: SummaryCall) => {
    setSelectedCallId(call.job_id);
    const { transcript, summary } = await fetchTranscript(call.job_id,user.token);
    setSelectedCall({
      id: call._id.$oid,
      jobId: call.job_id,
      patientName: call.metadata.name,
      phone: call.metadata.phone_number,
      callType: call.metadata.type,
      status: call.status,
      attempts: call.attempt_no,
      duration: call.call_duration_seconds,
      receivedAt: call.received_at,
      usage: call.usage,
      transcript,
      summary,
    });
  };

  // —— 导出 CSV —— //
  const handleDownloadCSV = (range?: Range[]) => {
    let exportData = allCalls;
    if (range?.[0].startDate && range![0].endDate) {
      const s = range[0].startDate!,
        e = range[0].endDate!;
      exportData = allCalls.filter((c) => isInRange(c, s, e));
    }
    if (!exportData.length) {
      alert("No records to export.");
      return;
    }

    const header = [
      "Job ID",
      "Patient Name",
      "Phone Number",
      "Call Type",
      "Status",
      "Attempts",
      "Duration (s)",
      "Received At",
      "Input Tokens",
      "Output Tokens",
    ];

    const rows = exportData.map((c) => {
      const prompt = c.usage?.llm_prompt_tokens ?? 0;
      const cached = c.usage?.llm_prompt_cached_tokens ?? 0;
      return [
        String(c.job_id),
        String(c.metadata.name),
        String(c.metadata.phone_number),
        String(c.metadata.type),
        String(c.status),
        String(c.attempt_no),
        String(c.call_duration_seconds),
        String(c.received_at),
        String(prompt - cached),
        String(c.usage?.llm_completion_tokens ?? 0),
      ];
    });

    const csv = [header, ...rows]
      .map((row) =>
        row
          .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");

    let suffix = "_all";
    if (range?.[0].startDate && range![0].endDate) {
      const sStr = format(range[0].startDate!, "yyyy-MM-dd");
      const eStr = format(range[0].endDate!, "yyyy-MM-dd");
      suffix = sStr === eStr ? `_${sStr}` : `_${sStr}_${eStr}`;
    }

    saveAs(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
      `calls_summary${suffix}.csv`
    );
  };

  // Download 弹窗
  const [showDownload, setShowDownload] = useState(false);
  const [dlTemp, setDlTemp] = useState<Range[]>([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);
  const dlRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dlRef.current && !dlRef.current.contains(e.target as Node)) {
        setShowDownload(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ---------- 快捷选择 ---------- */
  const applyLastNDays = (n: number) => {
    const end = new Date();
    const start = subDays(end, n - 1);
    const r: Range[] = [{ key: "selection", startDate: start, endDate: end }];
    setCalendarRange(r);
    setDateRange(r);
  };

  const clearFilter = () => {
    setDateRange(undefined);
    setCalendarRange([
      { key: "selection", startDate: new Date(), endDate: new Date() },
    ]);
  };

  // —— Total Calls（使用过滤后的 calls 数量）
  const totalCalls = calls.length;

  // —— 顶部“所选日期范围”文本 —— //
  const headerRangeLabel = (() => {
    const s = calendarRange?.[0]?.startDate;
    const e = calendarRange?.[0]?.endDate;
    if (s && e) return `${format(s, "MMM d, yyyy")} — ${format(e, "MMM d, yyyy")}`;
    return "Select a date range";
  })();

  return (
    <div className="relative p-8 bg-gray-50 min-h-screen">
      {/* <img
        src={IHPCLogo}
        alt="Institute of High Performance Computing"
        className="absolute top-6 right-6 w-40"
      />
      <h1 className="text-3xl font-bold mb-6">Medical Agent</h1> */}

      {/* 顶部三卡：左=自适应日历；中/右等高 */}
      <div className="grid grid-cols-3 gap-6 items-stretch">
        {/* 左：日历卡片 */}
        <div className={`${cardClass} min-h-[520px]`}>
          {/* 头部行：左标题 + 中心日期pill + 右侧 Clear */}
          <div className="flex items-center gap-3 mb-3">
            <div className="text-sm font-medium text-gray-600 whitespace-nowrap">
              Date Filter
            </div>

            <div className="flex-1 min-w-0 flex justify-center">
              <div
                className="inline-flex h-9 rounded-full border border-indigo-200 bg-indigo-50
                           px-4 items-center justify-center
                           text-sm font-medium text-indigo-700
                           text-center whitespace-nowrap"
                title={headerRangeLabel}
              >
                {headerRangeLabel}
              </div>
            </div>

            <button
              onClick={clearFilter}
              className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm whitespace-nowrap"
            >
              Clear
            </button>
          </div>

          <div className="flex-1">
            <AutoFitCalendar
              value={calendarRange}
              onChange={(r) => {
                setCalendarRange(r);
                setDateRange(r);
              }}
            />
          </div>

          {/* 快捷范围按钮：放在卡片底部 */}
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => applyLastNDays(7)}
              className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-sm"
            >
              Last 7 days
            </button>
            <button
              onClick={() => applyLastNDays(30)}
              className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-sm"
            >
              Last 30 days
            </button>
          </div>
        </div>

        {/* 中：Call Types（紧凑化） */}
        <div className={`${cardClass} min-h-[520px] p-5`}>
          <div className="flex items-center space-x-2">
            <ListTodo className="w-5 h-5 text-green-600" />
            <h3 className="text-[15px] font-medium text-gray-700">Call Types</h3>
          </div>
          <ul className="mt-3 px-1 divide-y divide-gray-100">
            {Object.entries(callTypes).map(([type, count]) => (
              <li
                key={type}
                className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0"
              >
                <span className="text-[15px] leading-6 text-gray-800">
                  {type}
                </span>
                <span className="text-[15px] leading-6 font-semibold tabular-nums text-gray-900 w-12 text-right">
                  {count}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex-1" />
        </div>

        {/* 右：Status Overview（紧凑化） */}
        <div className={`${cardClass} min-h-[520px] p-5`}>
          <div className="flex items-center space-x-2">
            <CheckCheck className="w-5 h-5 text-yellow-600" />
            <h3 className="text-[15px] font-medium text-gray-700">
              Status Overview
            </h3>
          </div>
          <ul className="mt-3 px-1 divide-y divide-gray-100">
            {Object.entries(statusTypes).map(([status, count]) => (
              <li
                key={status}
                className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0"
              >
                <span className="text-[15px] leading-6 text-gray-800">
                  {status}
                </span>
                <span className="text-[15px] leading-6 font-semibold tabular-nums text-gray-900 w-12 text-right">
                  {count}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex-1" />
        </div>
      </div>

      {/* —— 新的一排四个：Total Calls + 两个 Tokens + Scheduled Jobs —— */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mt-6">
        <div className="bg-white p-4 rounded-xl shadow-md flex items-center justify-between">
          <span className="font-medium">Total Calls</span>
          <span className="text-xl font-bold tabular-nums">
            {totalCalls.toLocaleString()}
          </span>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-md flex items-center justify-between">
          <span className="font-medium">Total Input Tokens</span>
          <span className="text-xl font-bold tabular-nums">
            {totalPromptTokens.toLocaleString()}
          </span>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-md flex items-center justify-between">
          <span className="font-medium">Total Output Tokens</span>
          <span className="text-xl font-bold tabular-nums">
            {totalCompletionTokens.toLocaleString()}
          </span>
        </div>

        <div className="relative" ref={jobsRef}>
          <button
            onClick={() => setShowJobsDropdown((v) => !v)}
            className="w-full bg-white p-4 rounded-xl shadow-md font-medium flex items-center justify-center"
          >
            Scheduled Jobs
          </button>
          {showJobsDropdown && (
            <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-lg z-50">
              <ScheduledJobs />
            </div>
          )}
        </div>
      </div>

      {/* Recent Calls + Details */}
      <div className="grid grid-cols-3 gap-6 mt-6">
        <div className="col-span-1">
          <h2 className="text-xl font-semibold mb-3">Recent Calls</h2>
          <RecentCallsList
            calls={calls}
            selectedCallId={selectedCallId}
            onSelect={handleSelectCall}
            loading={isLoading}
          />
        </div>

        <div className="col-span-2">
          <CallDetails data={selectedCall} />
        </div>
      </div>

      {/* Download Summary */}
      <div className="fixed bottom-6 left-6">
        <button
          onClick={() => setShowDownload(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow"
        >
          Download Summary
        </button>
        {showDownload && (
          <div
            ref={dlRef}
            className="absolute bottom-14 left-0 bg-white shadow-md rounded p-4"
          >
            <div className="flex justify-between mb-2">
              <span className="font-semibold">Select date range</span>
              <button
                className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200"
                onClick={() => {
                  handleDownloadCSV(undefined);
                  setShowDownload(false);
                }}
              >
                Download all
              </button>
            </div>
            <DateRange
              editableDateInputs
              moveRangeOnFirstSelection={false}
              ranges={dlTemp}
              onChange={(itm: RangeKeyDict) => setDlTemp([itm.selection])}
              maxDate={new Date()}
            />
            <div className="flex justify-end mt-2">
              <button
                onClick={() => {
                  handleDownloadCSV(dlTemp);
                  setShowDownload(false);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                Confirm
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
